import React, {useRef, useState, useEffect} from "react";
import {Link} from "react-router-dom";
import CollapsibleSection from "../components/CollapsibleSection";

const sections = [
  {key: "character", label: "Character Creation"},
  {key: "progression", label: "Progression"},
  {key: "downtime", label: "Downtime Activities"},
  {key: "inventory", label: "Inventory Management"},
  {key: "death", label: "Character Death"},
  {key: "safety", label: "Safety Tools"},
  {key: "agreement", label: "The Unspoken Agreement"},
  {key: "dm", label: "DM-ing Guidelines"},
];

const Rules: React.FC = () => {
  const sectionRefs = {
    character: useRef<HTMLDivElement>(null),
    progression: useRef<HTMLDivElement>(null),
    downtime: useRef<HTMLDivElement>(null),
    inventory: useRef<HTMLDivElement>(null),
    death: useRef<HTMLDivElement>(null),
    safety: useRef<HTMLDivElement>(null),
    agreement: useRef<HTMLDivElement>(null),
    dm: useRef<HTMLDivElement>(null),
  };

  const [active, setActive] = useState("character");

  useEffect(() => {
    const handleScroll = () => {
      const offsets = sections.map(({key}) => {
        const ref = sectionRefs[key as keyof typeof sectionRefs];
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          return {key, top: rect.top};
        }
        return {key, top: Infinity};
      });
      const visible = offsets.filter(
        (o) => o.top < window.innerHeight * 0.33 && o.top > -200
      );
      if (visible.length > 0) {
        setActive(visible[visible.length - 1].key);
      } else {
        setActive(sections[0].key);
      }
    };
    window.addEventListener("scroll", handleScroll, {passive: true});
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSidebarClick = (key: string) => {
    const ref = sectionRefs[key as keyof typeof sectionRefs];
    if (ref && ref.current) {
      ref.current.scrollIntoView({behavior: "smooth", block: "start"});
    }
    setActive(key);
  };

  return (
    <div className="rules-page sidebar-layout">
      {/* Sidebar */}
      <aside className="sidebar-aside">
        <div className="sidebar-header">CATEGORIES</div>
        <nav>
          {sections.map((section) => (
            <button
              key={section.key}
              onClick={() => handleSidebarClick(section.key)}
              className={`sidebar-link${active === section.key ? " sidebar-link-active" : ""}`}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="container rules-content">
        <h1>League Rules</h1>
        <p className="flavor-text">
          Everything you need to know about playing in the League of Fabled
          Pursuits.
        </p>

        {/* Character Creation */}
        <section
          ref={sectionRefs.character}
          className="rules-section"
          style={{paddingTop: "0.5rem"}}
        >
          <h2>Character Creation</h2>
          <h3>Source Books</h3>
          <p>
            You may create your character using the content of the new core
            manuals published by Wizards of the Coast in 2024 and that of any
            official D&D 5e books that haven't yet been updated. The two
            editions are compatible. You can refer to page 38 of the PHB to
            learn how you can adapt 5e Species (‘Races’) and Backgrounds to work
            with 2024 rules. As you read the rest of these rules, any reference
            to a book refers to its most recent edition as of January 2026.
          </p>
          <h3>Starting Level and Number of Characters</h3>
          <p className="flavor-text">
            Every adventurer started from the bottom: level 1. They eventually
            realised that not starting from the bottom was also an option.
          </p>
          <ul>
            <li>Your first League character will start at level 1.</li>
            <li>
              Going forward, you will be able to create characters up to one
              level below that of your highest-level character.
            </li>
            <li>
              Whenever you create a new character, you must play one session
              with them before creating another one.
            </li>
          </ul>
          <h3>Determining Ability Scores</h3>
          <p className="flavor-text">
            Adventurers are shaped by their past. Their past is mostly shaped by
            chance.
          </p>
          <ul>
            <li>
              You may determine your character's Ability Scores in one of the
              following ways (pg. 38 of the PHB):
              <ul>
                <li>Standard Array and Standard Array by Class.</li>
                <li>Point Cost.</li>
                <li>
                  Random Generation (i.e., 'Stat Rolling'). Unhappy with your
                  rolled stats? You get to reroll all of them (not just the bad
                  rolls) once, but you have to take the new results.
                </li>
              </ul>
            </li>
          </ul>
          <h3>Determining HP</h3>
          <p className="flavor-text">
            How long can your adventurer stand the heat?
          </p>
          <p>
            At character creation, your character starts with an amount of HP
            determined by their class (pg. 40 of the PHB). Choose one of the
            following methods to determine your character's HP beyond level 1:
          </p>
          <ul>
            <li>Fixed Hit Point by Class (pg. 42 of the PHB).</li>
            <li>
              Upon gaining each level, roll one Hit Die and add your
              Constitution modifier to the result. Add the total (minimum of 1)
              to your character's maximum HP (pg. 42 of the PHB).
            </li>
          </ul>
          <h3>Respeccing</h3>
          <p className="flavor-text">
            It's never too late to discover who you truly are! Unless you are
            level 5.
          </p>
          <ul>
            <li>
              Until your character reaches level 5, you are free to change
              anything about them except their Species and Class.
            </li>
            <li>Once your character reaches level 5, no more respeccing.</li>
            <li>
              If you create a character at or above level 5, you may respec them
              until the start of their second session.
            </li>
          </ul>
        </section>
        <hr className="rules-divider" />

        {/* Progression */}
        <section ref={sectionRefs.progression} className="rules-section">
          <h2>The Progression System</h2>
          <h3>Levelling Up</h3>
          <p className="flavor-text">
            It's not about EXP, it's about experiences.
          </p>
          <ul>
            <li>Characters level up by spending Memento Tokens.</li>
            <li>
              At the end of a session, each character earns one Token, and the
              DM earns a Token to use on any of their characters.
            </li>
            <li>
              Each character must play at least one session at each level before
              leveling up to the next.
            </li>
          </ul>
          <table className="rules-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Level Range</th>
                <th>Tokens req.</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>1-4</td>
                <td>1 per level</td>
              </tr>
              <tr>
                <td>2</td>
                <td>5-8</td>
                <td>2 per level</td>
              </tr>
              <tr>
                <td>3</td>
                <td>9-12</td>
                <td>3 per level</td>
              </tr>
              <tr>
                <td>4</td>
                <td>13-16</td>
                <td>4 per level</td>
              </tr>
              <tr>
                <td>5</td>
                <td>17-20</td>
                <td>5 per level</td>
              </tr>
            </tbody>
          </table>
        </section>
        <hr className="rules-divider" />

        {/* Downtime Activities */}
        <section ref={sectionRefs.downtime} className="rules-section">
          <h2>Downtime Activities</h2>
          <p className="flavor-text">
            Life is not only about adventuring. It's also about preparing for
            the next adventure.
          </p>
          <p>
            Characters earn 10 Downtime Days upon completion of each session.
            Downtime Days are spent between sessions, doing{" "}
            <a href="#standard-downtime">Standard</a> or{" "}
            <a href="#special-downtime">Special</a> Downtime activities.
          </p>
          <p>
            Downtime Days can be accumulated and spent later, but cannot be
            spent before they have been earned, nor can earned days be
            transferred between characters.
          </p>
          <p>
            All character activities that take place outside sessions must
            utilise Downtime Days.
          </p>
          <h4 id="non-stacking-rule">Non-Stacking Rule</h4>
          <p>
            Multiple Downtime activities cannot be performed simultaneously.
          </p>
          <p>
            Some Downtime activities can be sped up through Character
            collaboration or use of hirelings, by sharing the time requirement
            evenly. In any such case, no more than one helper can be employed at
            a time.
          </p>
          <p>
            Other features (e.g. bastions, spells, magic items) cannot reduce
            Downtime Days, gold costs, or crafting time.
          </p>
          <h4 id="record-keeping">Record Keeping</h4>
          <p>
            Players are responsible for recording Downtime Days spent via their{" "}
            <a href="/characters">character sheet</a> on The League of Fabled
            Pursuits website.
          </p>
          <p>All Downtime activities must be recorded.</p>
          <h3 id="standard-downtime">Standard Downtime</h3>
          <p>
            Standard Downtime are a list of standard activities that are
            available to all characters and does not require DM oversight,
            unless explicitly stated. Each activity has a fixed Downtime cost
            and possibly a gold cost, and these may not be altered or adjusted,
            even by DM ruling.
          </p>
          <p>Standard Downtime activities include:</p>
          <CollapsibleSection title="Generic Work" id="generic-work">
            <p>
              Generic Work is a{" "}
              <a href="#standard-downtime">Standard Downtime activity</a> to
              convert Downtime Days into gold. The downtime day-to-gold ratio
              increases with each tier of play, in accordance with the table
              below:
            </p>
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Gold per 5 Downtime Days</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>75</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>250</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>500</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>1,000</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>2,000</td>
                </tr>
              </tbody>
            </table>
          </CollapsibleSection>

          <CollapsibleSection
            title="Scribing Spell Scrolls"
            id="scribing-spell-scrolls"
          >
            <p>
              Scribing a spell scroll is a{" "}
              <a href="#standard-downtime">Standard Downtime activity</a>.
              Scribing a spell scroll takes an amount of time and money related
              to the level of the spell the character wants to scribe.
            </p>
            <h4>Eligibility</h4>
            <p>
              To scribe a spell scroll, a character must have proficiency in
              Arcana and have the associated spell prepared (or be among the
              character’s known spells). Each character has their own language
              and style for scribing spells, therefore cannot benefit from Help.
            </p>
            <h4>Downtime Cost</h4>
            <p>
              The Downtime Days and gold cost are based on the spell level (see
              table below). Some spells require material components, which the
              scriber must provide. If the spell requires a consumable material
              component, it must be purchased separately at normal cost and is
              consumed in the scribing process.
            </p>
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Spell Level</th>
                  <th>Downtime Days</th>
                  <th>Cost (GP)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Cantrip</td>
                  <td>1</td>
                  <td>15</td>
                </tr>
                <tr>
                  <td>1</td>
                  <td>2</td>
                  <td>50</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>3</td>
                  <td>100</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>5</td>
                  <td>250</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>10</td>
                  <td>500</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>15</td>
                  <td>1,500</td>
                </tr>
                <tr>
                  <td>6</td>
                  <td>30</td>
                  <td>3,500</td>
                </tr>
                <tr>
                  <td>7</td>
                  <td>60</td>
                  <td>8,500</td>
                </tr>
                <tr>
                  <td>8</td>
                  <td>90</td>
                  <td>20,000</td>
                </tr>
                <tr>
                  <td>9</td>
                  <td>120</td>
                  <td>50,000</td>
                </tr>
              </tbody>
            </table>
          </CollapsibleSection>

          <CollapsibleSection title="Copying Spells" id="copying-spells">
            <p>
              Copying a spell from a scroll or another spell book is a{" "}
              <a href="#standard-downtime">Standard Downtime activity</a>.
            </p>
            <h4>Eligibility</h4>
            <p>
              To copy a spell into a spellbook, a character must have a class
              feature that allows copying of spells and have collected all
              necessary materials. They must also be able to prepare the level
              of spell that is being copied. If copying from another player
              character’s spellbook, both characters must have played at least
              one session with each other.
            </p>
            <h4>Downtime Cost</h4>
            <p>
              Use Rules as Written to determine the time and money cost required
              to copy spells (e.g. for the Wizard class: PHB page 167). Multiple
              spells can be copied in a single sitting, adding together the
              hours required. The Downtime Days consumed is the sum of those
              hours divided by 8 and is always rounded up. When copying from
              another player character’s spellbook, both characters must expend
              the full Downtime Days (for the copying and for making a spellbook
              available).
            </p>
          </CollapsibleSection>

          <CollapsibleSection title="Trading Items" id="trading-items">
            <p>
              Trading items between characters is a{" "}
              <a href="#standard-downtime">Standard Downtime activity</a>. To be
              eligible, characters must be of the same Tier, and must have
              played at least one session together. Legendary items can only be
              passed between Tier 4+ characters. This activity can be used to
              gift one item from one character to another, or to exchange one
              item each between characters.
            </p>
            <p>
              Non-magical items require 5 Downtime Days, and magic items require
              10 Downtime Days. Both characters spend the Downtime Days,
              regardless of whether it is a gifting or an exchange.
            </p>
          </CollapsibleSection>

          <CollapsibleSection title="Crafting Items" id="crafting-items">
            <p>
              Crafting items is a{" "}
              <a href="#standard-downtime">Standard Downtime activity</a>.
            </p>
            <h4 id="crafting-nonmagical-items">Crafting Nonmagical Items</h4>
            <p>
              Characters may craft nonmagical items following the rules in the
              PHB (p 233), and must comply with requirements for Downtime Days,
              gold cost and tools / proficiencies.
            </p>
            <h4 id="crafting-magic-items">Crafting Magic Items</h4>
            <p>
              Characters may craft magic items during downtime in accordance
              with the DMG (pp 220-221).
            </p>
            <h5>Eligibility</h5>
            <p>
              To craft a magic item, a character must meet all prerequisites
              specified in the DMG, including tool and skill proficiencies,
              payment of gold and using Downtime Days.
            </p>
            <h5>Downtime Days</h5>
            <p>
              Downtime requirements are based on item rarity, as shown in the
              DMG p 221. Characters must spend the required Downtime Days in a
              single sitting. Helpers (League characters or hirelings) split the
              Downtime burden evenly, but must meet the same eligibility
              requirements in order to be able to help. A maximum of one helper
              can aid at a time in the crafting process.
            </p>
            <h5>Materials and Special Requirements</h5>
            <p>
              Access to necessary materials may be obtained through play or as a
              different <a href="#standard-downtime">Downtime activity</a>. In a
              city, there is a 75% chance that the necessary materials are found
              in shops, and in any other settlement that chance is 25%. Roll a
              d100, if the result is 75 or lower, you succeed. Otherwise, the
              materials aren’t available, and you must spend 7 downtime days to
              search again. If the crafting incorporates another base item (e.g.
              to enchant magical armour), it must be purchased separately at
              normal cost or crafted separately.
            </p>
          </CollapsibleSection>

          <CollapsibleSection
            title="Spellcasting Services"
            id="spellcasting-services"
          >
            <p>
              Making use of Spellcasting Services is a{" "}
              <a href="#standard-downtime">Standard Downtime activity</a>. The
              character must pay the gold value per the table below, and provide
              any consumable material components the spell requires. The
              Spellcasting Service provider can offer the necessary components,
              at the normal cost. NPC cannot perform spellcasting services above
              level 6 in downtime.
            </p>
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Spell Level</th>
                  <th>Downtime Days</th>
                  <th>Cost (GP)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Cantrip</td>
                  <td>1</td>
                  <td>30</td>
                </tr>
                <tr>
                  <td>1</td>
                  <td>1</td>
                  <td>100</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>1</td>
                  <td>200</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>1</td>
                  <td>500</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>2</td>
                  <td>1,000</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>3</td>
                  <td>3,000</td>
                </tr>
                <tr>
                  <td>6</td>
                  <td>5</td>
                  <td>7,000</td>
                </tr>
              </tbody>
            </table>
          </CollapsibleSection>

          <h3 id="special-downtime">Special Downtime</h3>
          <p>
            Special Downtime activities are administered by individual DMs
            within their own Titan or other setting. Such activities are
            available to players who have completed at least 1 session in that
            setting, plus any other conditions the DM deems appropriate. DMs
            determine the activities they make available, and can change them at
            any time.
          </p>
          <p>
            Where Special Downtime activities require dice rolls to determine
            outcomes, this must be overseen by that DM at a game table or within
            an appropriate Discord channel.
          </p>
          <p>
            Special Downtime activities cannot bypass, replace or alter the
            costs or Downtime requirements of Standard Downtime activities.
          </p>
          <p>Special Downtime activities include:</p>

          <CollapsibleSection title="Casting Spells" id="casting-spells">
            <p>
              Casting spells between sessions is a{" "}
              <a href="#special-downtime">Special Downtime activity</a>.
            </p>
            <h4>Downtime Days</h4>
            <p>
              Spells casting as a Downtime activity consumes 1 Downtime Day,
              provided the casting time is not more than 8 hours. Spells that
              have longer casting times consume a number of Downtime Days equal
              to the duration of casting in hours, divided by 8 and is always
              rounded up. If the spell involves or effects another player
              character, they are also required to spend the same number of
              Downtime Days. The normal verbal, somatic and material
              requirements of the spell must be met.
            </p>
            <h4>Raising the Dead</h4>
            <p>
              Spells that return a character to life between sessions are also{" "}
              <a href="#special-downtime">Special Downtime activities</a> and
              must be overseen by a DM.
            </p>
            <p>
              In addition, the raised subject must spend Downtime Days
              recuperating from their ordeal, equalling half their current level
              (rounded down) multiplied by 10. If they have enough Downtime Days
              accumulated, this can be spent immediately. If they do not have
              enough, it must be consumed as it is earned from their following
              adventures.
            </p>
            <p>
              The recipient's Downtime cost reflects recovery time. A character
              who has been raised cannot undertake any other Downtime activity
              during this recovery period.
            </p>
          </CollapsibleSection>

          <CollapsibleSection title="Bastions" id="bastions">
            <p>
              Taking a Bastion turn is a{" "}
              <a href="#special-downtime">Special Downtime activity</a>,
              administered by individual DMs within their own Titan or setting,
              primarily in accordance with the 2024 Bastion rules.
            </p>
            <h4>Eligibility</h4>
            <p>
              To take a Bastion turn, the relevant DM must approve a bastion
              being created, and characters must have played a session on the
              relevant Titan or setting.
            </p>
            <h4>Downtime Cost</h4>
            <p>
              Bastion Turns cost 10 Downtime Days and are considered a Downtime
              activity.
            </p>
            <h4>DM Oversight</h4>
            <p>
              All Bastion activity must be overseen by the DM of the Titan or
              setting in which the Bastion is located. Dice rolls for Bastion
              events, facility functions, or other outcomes must be made within
              an appropriate Discord channel under that DM's supervision.
            </p>
            <h4>Restrictions</h4>
            <p>
              Bastions cannot reduce the Downtime Days, gold costs, or crafting
              time of any Standard Downtime activity. Where a Bastion facility
              produces an outcome that is equivalent to a Standard Downtime
              activity (e.g. <a href="#scribing-spell-scrolls">spell scrolls</a>
              , <a href="#crafting-items">crafted item</a>,{" "}
              <a href="#spellcasting-services">spellcasting services</a>, etc.),
              that output is subject to all League rules governing the
              equivalent Standard activity. This includes eligibility
              requirements, material component costs, and any other conditions
              specified in the Standard Downtime rules. Bastion facilities
              cannot grant access to outcomes that a character would
              specifically be ineligible for per Standard downtime activity
              rules.
            </p>
          </CollapsibleSection>
        </section>
        <hr className="rules-divider" />

        {/* Inventory Management */}
        <section ref={sectionRefs.inventory} className="rules-section">
          <h2>Inventory</h2>
          <h3>Carrying Capacity</h3>
          <p className="flavor-text">Who uses Encumbrance anyway?</p>
          <p>
            The rules for Carrying Capacity apply as described in the Rules
            Glossary at pg. 362 of the PHB. In short, it is usually assumed that
            your character can carry their equipment and items, but a check
            might be required when some items carried, pushed or pulled are
            particularly heavy or cumbersome.
          </p>
        </section>
        <hr className="rules-divider" />

        {/* Character Death */}
        <section ref={sectionRefs.death} className="rules-section">
          <h2>A Character's Death</h2>
          <h3>Wills</h3>
          <p className="flavor-text">
            When you hear the beating of wings, it might be too late to list
            everything.
          </p>
          <p>
            At any point during their adventuring career, you may write a will
            for your character. Only characters they have met during play may be
            mentioned in their will. If your character dies, they will first
            lose:
          </p>
          <ul>
            <li>30% of their gold (rounded up, minimum of 1).</li>
            <li>
              30% of their magic items (rounded up, minimum of 1, chosen
              randomly).
            </li>
            <li>
              30% of their consumables (rounded up, minimum of 1, chosen
              randomly).
            </li>
          </ul>
          <p>The will will then be applied to the rest of their belongings.</p>
          <p>
            <strong>Note:</strong> any belongings not mentioned in your
            character's will will also be lost!
          </p>
          <h3>Making a Replacement Character</h3>
          <p className="flavor-text">
            Just because your character's adventures have ended, doesn't mean
            yours should.
          </p>
          <p>
            When one of your characters dies, you may choose one of the
            following options:
          </p>
          <ul>
            <li>
              Create a new character one level lower than the one you lost (and
              lose all of the dead character's Memento Tokens).
            </li>
            <li>
              Create a level 1 character with an amount of Memento Tokens equal
              to the levels of the character you just lost.
            </li>
            <li>
              If you have (or had) at least one level 10 character, create a
              level 5 character with Memento Tokens equal to the levels of the
              character you just lost.
            </li>
          </ul>
        </section>
        <hr className="rules-divider" />

        {/* Safety Tools */}
        <section ref={sectionRefs.safety} className="rules-section">
          <h2>Safety at the Table</h2>
          <p>
            Every DM is free to take whatever measures they see fit to make sure
            everyone is comfortable at their table, but here are some
            suggestions that should work in almost any situation.
          </p>
          <h3>The X-Card</h3>
          <ul>
            <li>
              The DM can place a card in the middle of the table with an X drawn
              on it.
            </li>
            <li>
              When the session starts, the DM informs the players clearly about
              it and how it works:
            </li>
            <li>
              Whenever a player touches the X-Card, the current scene will be
              skipped with no questions asked.
            </li>
          </ul>
          <p>
            <strong>Note:</strong> If all players at the table confirm they know
            about the X-Card, the DM is then free to skip the initial
            explanation.
          </p>
          <h3>The Walkout</h3>
          <p>
            At any given time, any player is free to leave the room or the
            vicinity of the table until the end of a scene that they prefer not
            to be part of. Just like with touching the X-Card, this action
            should not be questioned as long as it's performed in respect of
            everyone else at the table.
          </p>
          <h3>Feedback</h3>
          <p>
            We welcome both public and anonymous feedback. For more information,
            check out our <Link to="/feedback">Feedback section</Link>.
          </p>
        </section>
        <hr className="rules-divider" />

        {/* The Unspoken Agreement */}
        <section ref={sectionRefs.agreement} className="rules-section">
          <h2>The Unspoken Agreement</h2>
          <p>
            We are the writers of our collaborative stories, where unlikely
            teams of adventurers come together to achieve common goals. As such,
            when we join a table, we implicitly agree to work together with our
            party. While our characters' motivations don't have to be strong and
            dramatic, our actions should remain cooperative and helpful.
          </p>
          <p>
            If a player consistently takes actions against the party's goals,
            their DM should remind them of this agreement. If this continues
            despite the reminder, the DM is free to remove the player from the
            table or take any other measures to ensure everyone else can keep
            having fun.
          </p>
          <p>
            <strong>NOTE:</strong> When any conflict or uncomfortable situation
            arises at the table, the DM is required to inform The League's Core
            Team as soon as possible. This way, the Team can help handle the
            aftermath and learn how to prevent similar situations in the future.
          </p>
        </section>
        <hr className="rules-divider" />

        {/* DM-ing Guidelines */}
        <section ref={sectionRefs.dm} className="rules-section">
          <h2>DMing for the League</h2>
          <h3>How to become a DM</h3>
          <p className="flavor-text">Oh, look! Another way to play!</p>
          <ul>
            <li>
              Being a League DM means being able to arrange and log sessions on
              this website. These sessions should follow the rules stated below,
              as well as any rules that apply to players.
            </li>
            <li>
              To become an official League DM, simply select it during signup,
              or click "Become a DM" in the player dashboard.
            </li>
            <li>
              Once sent, your request will be reviewed by another League DM, and
              as soon as it is approved you will have access to the DM areas of
              the website. You can now start DMing League sessions!
            </li>
            <li>
              For every session you run, you will gain one Memento Token which
              you can use on any of your characters.
            </li>
          </ul>
          <h3>Awarding treasure</h3>
          <p className="flavor-text">
            Or how to make sure your adventurers don't murder each other.
          </p>
          <ul>
            <li>
              'Treasure' is any amount of gold or other spendable currency
              awarded to the PCs, plus any items that have a high monetary value
              (except Magic Items - see below).
            </li>
            <li>
              There are ranges for the total value of treasure you can award to
              a party each session, listed in the table below.
            </li>
          </ul>
          <table className="rules-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Treasure allowance per session</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1 (levels 1-4)</td>
                <td>100 - 500 gp</td>
              </tr>
              <tr>
                <td>2 (levels 5-8)</td>
                <td>500 - 3,000 gp</td>
              </tr>
              <tr>
                <td>3 (levels 9-12)</td>
                <td>5,000 - 10,000 gp</td>
              </tr>
              <tr>
                <td>4 (levels 13-16)</td>
                <td>10,000 - 50,000 gp</td>
              </tr>
              <tr>
                <td>5 (levels 17-20)</td>
                <td>50,000 - 100,000 gp</td>
              </tr>
            </tbody>
          </table>
          <ul>
            <li>
              Single items that have a high monetary value can be used or traded
              during the same session.
            </li>
            <li>
              At the end of the session, convert any remaining treasure item
              into gold, then split this equally among the party.
            </li>
          </ul>
          <h3>Awarding Magic Items</h3>
          <p className="flavor-text">
            Or how to make sure your adventurers don't murder you.
          </p>
          <ul>
            <li>
              DMs are free to award any Magic Items they like and as many as
              they see fit during a session, as long as this follows the rules
              specified for players and general common sense (e.g., avoiding
              blatant favouritism).
            </li>
            <li>
              Each player is allowed to trade the Memento Token they would
              obtain from the current session for an additional 'copy' of one
              Magic Item.
            </li>
            <li>
              OR the gold value of the Magic Item can be divided by the amount
              of PCs in the party and each PC that is not keeping the item can
              receive that much gold.
            </li>
            <li>
              As a DM, always encourage reasonable discussions and diplomacy
              when splitting loot.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default Rules;
